/**
 * Phase 12b2 — Campaign publish + performance panel.
 *
 * Self-contained card that:
 *   1. Fetches the Campaign aggregate + (if published) `/performance`.
 *   2. Shows a Publish button gated by:
 *      approval_status === "approved"
 *      && publish_status !== "published"
 *      && user has `campaigns.publish`
 *      && (for ad-platform campaigns) a connector connection exists.
 *   3. Renders a 7-day performance sparkline with Recharts when
 *      publish_status === "published".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api, auth } from "../../lib/api";
import { showAgentError } from "./AgentErrorToast";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Rocket, ExternalLink, CheckCircle2 } from "lucide-react";

const AD_PLATFORMS = new Set([
  "meta", "facebook", "instagram",
  "google_search", "google_display", "youtube",
  "linkedin", "tiktok",
]);

const COPY = {
  de: {
    title: "Veröffentlichung", publish: "Auf {platform} veröffentlichen",
    publishing: "Veröffentliche…",
    confirm_title: "Kampagne veröffentlichen?",
    confirm_body:
      "Die Kampagne wird an die verbundene Ad-Plattform gesendet " +
      "(aktuell Mock). Diese Aktion ist im Cockpit sichtbar.",
    published: "Veröffentlicht", open_ad: "In Ads-Manager öffnen",
    needs_approval: "Warten auf Freigabe — kein Publish möglich.",
    needs_connection: "Ad-Plattform nicht verbunden. Bitte zuerst verbinden.",
    perf_title: "Letzte 7 Tage",
    imp: "Impressions", clk: "Klicks", spend: "Ausgaben",
    cpm: "CPM", ctr: "CTR",
    loading: "Lade…", cancel: "Abbrechen", confirm: "Veröffentlichen",
  },
  en: {
    title: "Publish", publish: "Publish to {platform}",
    publishing: "Publishing…",
    confirm_title: "Publish this campaign?",
    confirm_body:
      "The campaign will be sent to the connected ad platform " +
      "(currently mock). This action is visible in the Cockpit.",
    published: "Published", open_ad: "Open in Ads Manager",
    needs_approval: "Awaiting approval — publish is blocked.",
    needs_connection: "Ad platform not connected. Connect it first.",
    perf_title: "Last 7 days",
    imp: "Impressions", clk: "Clicks", spend: "Spend",
    cpm: "CPM", ctr: "CTR",
    loading: "Loading…", cancel: "Cancel", confirm: "Publish",
  },
};

function firstAdPlatform(platforms) {
  for (const p of platforms || []) {
    if (AD_PLATFORMS.has(p)) {
      if (p === "facebook" || p === "instagram") return "meta";
      if (p.startsWith("google") || p === "youtube") return "google";
      return p;
    }
  }
  return null;
}

export default function CampaignPublishPanel({ campaignId, locale = "de" }) {
  const t = COPY[locale] || COPY.en;
  const [campaign, setCampaign] = useState(null);
  const [platformsRow, setPlatformsRow] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refetch, setRefetch] = useState(0);

  const canPublish = auth.hasPermission("campaigns.publish");
  const adPlatform = campaign ? firstAdPlatform(campaign.platforms) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ap] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get("/ad-platforms"),
      ]);
      setCampaign(c.data);
      setPlatformsRow(ap.data);
      if (c.data?.publish_status === "published") {
        try {
          const p = await api.get(
            `/campaigns/${campaignId}/performance?days=7`);
          setPerf(p.data);
        } catch (_e) { /* performance is a soft dependency */ }
      } else {
        setPerf(null);
      }
    } catch (_e) {
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load, refetch]);

  const connectionExists = useMemo(() => {
    if (!adPlatform) return true;  // non-ad platforms never need a connection
    const pl = (platformsRow?.platforms || []).find(
      (p) => p.platform === adPlatform);
    return (pl?.connections || []).some((c) => c.status === "connected");
  }, [platformsRow, adPlatform]);

  async function doPublish() {
    setBusy(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/publish`);
      toast.success(
        `${t.published}: ${r.data?.external_url || r.data?.name || "OK"}`);
      setConfirmOpen(false);
      setRefetch((x) => x + 1);
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally { setBusy(false); }
  }

  if (loading) {
    return (
      <Card data-testid="campaign-publish-panel-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t.loading}
        </CardContent>
      </Card>
    );
  }
  if (!campaign) return null;

  const published = campaign.publish_status === "published";
  const approved = campaign.approval_status === "approved";
  const showPublish = !published && approved && canPublish && connectionExists;

  return (
    <Card data-testid="campaign-publish-panel">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base inline-flex items-center gap-2">
            <Rocket className="size-4" />
            {t.title}
          </CardTitle>
          {published && (
            <div className="mt-1 flex items-center gap-2">
              <Badge
                data-testid="campaign-publish-badge"
                variant="outline"
                className="border-emerald-300 bg-emerald-50 text-emerald-800"
              >
                <CheckCircle2 className="size-3 mr-1" />
                {t.published}
              </Badge>
              {campaign.external_url && (
                <a
                  href={campaign.external_url} target="_blank"
                  rel="noreferrer"
                  data-testid="campaign-external-url"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="size-3" />
                  {t.open_ad}
                </a>
              )}
            </div>
          )}
          {!approved && !published && (
            <div
              data-testid="campaign-publish-needs-approval"
              className="text-xs text-muted-foreground mt-1"
            >
              {t.needs_approval}
            </div>
          )}
          {approved && !published && !connectionExists && (
            <div
              data-testid="campaign-publish-needs-connection"
              className="text-xs text-rose-600 mt-1"
            >
              {t.needs_connection}
            </div>
          )}
        </div>
        {showPublish && (
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            data-testid="campaign-publish-btn"
          >
            <Rocket className="size-4 mr-2" />
            {t.publish.replace("{platform}", adPlatform || "n/a")}
          </Button>
        )}
      </CardHeader>

      {perf && (
        <CardContent className="space-y-3" data-testid="campaign-perf-block">
          <div className="text-xs uppercase text-muted-foreground">
            {t.perf_title}
          </div>
          <div
            className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs"
            data-testid="campaign-perf-totals"
          >
            <div>
              <div className="text-muted-foreground">{t.imp}</div>
              <div
                className="text-base font-semibold tabular-nums"
                data-testid="campaign-perf-impressions"
              >{perf.impressions?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-muted-foreground">{t.clk}</div>
              <div className="text-base font-semibold tabular-nums"
                    data-testid="campaign-perf-clicks">
                {perf.clicks?.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">{t.spend}</div>
              <div className="text-base font-semibold tabular-nums">
                ${perf.spend_usd?.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">{t.cpm}</div>
              <div className="text-base font-semibold tabular-nums">
                ${perf.cpm?.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">{t.ctr}</div>
              <div className="text-base font-semibold tabular-nums">
                {(perf.ctr * 100).toFixed(2)}%
              </div>
            </div>
          </div>
          <div
            data-testid="campaign-perf-chart"
            className="h-40 w-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={perf.breakdown_by_day || []}
                             margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "10px" }} />
                <Line type="monotone" dataKey="impressions"
                          stroke="#4f46e5" strokeWidth={2} dot={false}
                          name={t.imp} />
                <Line type="monotone" dataKey="clicks"
                          stroke="#059669" strokeWidth={2} dot={false}
                          name={t.clk} />
                <Line type="monotone" dataKey="spend_usd"
                          stroke="#dc2626" strokeWidth={2} dot={false}
                          name={t.spend} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="campaign-publish-confirm">
          <DialogHeader>
            <DialogTitle>{t.confirm_title}</DialogTitle>
            <DialogDescription>{t.confirm_body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
                     disabled={busy}
                     data-testid="campaign-publish-cancel">{t.cancel}</Button>
            <Button onClick={doPublish} disabled={busy}
                     data-testid="campaign-publish-confirm-btn">
              {busy ? t.publishing : t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
